/**
 * Vulnerability Scan Handlers Hook
 *
 * Provides scan action handlers for vulnerabilities including:
 * - Rescanning affected assets
 * - Confirming asset affiliation
 *
 * @module sections/vulnerabilities/hooks/actions
 */

import { toast } from 'sonner';

import { useRunAffiliation } from '@/hooks/affiliation';
import { useAxios } from '@/hooks/useAxios';
import { useBulkCapabilityJob } from '@/hooks/useCapability';
import { HackerOneSubmitParams } from '@/sections/vulnerabilities/components/modals/HackerOneSubmitModal';
import { VulnerabilityScanHandlers } from '@/sections/vulnerabilities/types/action.types';
import { Risk } from '@/types';

/**
 * Custom hook for vulnerability scan handlers
 * Orchestrates scan execution for vulnerabilities and their affected assets
 *
 * @param onScanComplete - Optional callback to invoke after scans are submitted
 * @returns Object containing scan handler functions
 */
export const useVulnerabilityScanHandlers = (
  onScanComplete?: () => void
): VulnerabilityScanHandlers => {
  const axios = useAxios(); // Used by handleConfirmAffiliation
  const { mutateAsync: runJobs } = useBulkCapabilityJob();
  const { mutateAsync: runAffiliation } = useRunAffiliation();

  /**
   * Trigger rescans on entities affected by selected vulnerabilities
   *
   * Process:
   * 1. Send risk keys directly to the job endpoint
   * 2. Backend automatically finds entities that have HAS_VULNERABILITY relationship to each risk
   *    - This includes Assets with direct vulnerabilities (Asset -[HAS_VULNERABILITY]-> Risk)
   *    - And Ports with vulnerabilities (Port -[HAS_VULNERABILITY]-> Risk)
   * 3. Backend creates appropriate scan jobs for each affected entity type
   * 4. Jobs are filtered to only use the same capability that originally found the vulnerability
   *
   * @param vulnerabilities - Vulnerabilities to rescan affected entities for
   */
  const handleRescan = async (vulnerabilities: Risk[]): Promise<void> => {
    try {
      // Build scan jobs with risk keys - backend handles finding affected entities
      // The backend's risk handler (pkg/services/job/service.go:118-157) will:
      // 1. Query for all entities with HAS_VULNERABILITY to each risk
      // 2. Create appropriate jobs for each entity type (Asset, Port, etc.)
      // 3. Filter jobs to match the risk's source capability for targeted rescanning
      const jobs = vulnerabilities.map(vulnerability => ({
        assetKey: vulnerability.key, // Actually a risk key - backend detects #risk# prefix
        // No capabilities specified = backend uses risk's source capability
      }));

      // Trigger scans via existing proven mechanism
      // Note: useBulkCapabilityJob mutation handles success toast notification
      await runJobs(jobs);

      // Invalidate queries to refresh vulnerability table
      onScanComplete?.();
    } catch (error) {
      console.error('[useVulnerabilityScanHandlers] Rescan failed:', error);
      toast.error('Failed to initiate rescan');
    }
  };

  /**
   * Confirm asset affiliation for selected vulnerabilities
   *
   * Process:
   * 1. Query graph for assets affected by each vulnerability using HAS_VULNERABILITY relationship
   * 2. Extract unique affected asset keys from graph query results
   * 3. Run affiliation checks on each asset
   * 4. Validate asset ownership
   *
   * @param vulnerabilities - Vulnerabilities to confirm affiliation for
   */
  const handleConfirmAffiliation = async (
    vulnerabilities: Risk[]
  ): Promise<void> => {
    try {
      const assetKeys = new Set<string>();
      const fetchErrors: string[] = [];

      // Show loading feedback
      toast.loading(
        `Finding assets affected by ${vulnerabilities.length} vulnerabilit${
          vulnerabilities.length > 1 ? 'ies' : 'y'
        }...`,
        { id: 'affiliation-fetch' }
      );

      // Use graph query via /my endpoint to find affected assets
      // Pattern: Query for Assets that have HAS_VULNERABILITY relationship to specific Risk
      for (const vulnerability of vulnerabilities) {
        try {
          const { data } = await axios.post('/my', {
            node: {
              labels: ['Asset'],
              relationships: [
                {
                  label: ['HAS_VULNERABILITY'],
                  target: {
                    labels: ['Risk'],
                    filters: [
                      {
                        field: 'key',
                        operator: '=',
                        value: vulnerability.key,
                      },
                    ],
                  },
                },
              ],
            },
          });

          const assets = data?.assets || [];
          assets.forEach((asset: { key: string }) => {
            // Only add if it's actually an Asset key (not Attribute)
            // Asset keys start with #asset# while Attribute keys start with #attribute#
            if (asset.key.startsWith('#asset#')) {
              assetKeys.add(asset.key);
            }
          });
        } catch (error) {
          fetchErrors.push(vulnerability.key);
          console.error(
            `[useVulnerabilityScanHandlers] Failed to fetch assets for vulnerability ${vulnerability.key}:`,
            error
          );
        }
      }

      toast.dismiss('affiliation-fetch');

      // Enhanced error handling with specific feedback
      if (fetchErrors.length === vulnerabilities.length) {
        toast.error('Failed to fetch affected assets for all vulnerabilities');
        return;
      }

      if (assetKeys.size === 0) {
        if (fetchErrors.length > 0) {
          toast.warning(
            `No assets found. ${fetchErrors.length} of ${vulnerabilities.length} vulnerabilities had errors.`
          );
        } else {
          toast.warning(
            'No affected assets found for selected vulnerabilities'
          );
        }
        return;
      }

      // Run affiliation checks for each asset
      // Note: runAffiliation already shows success toast, so we don't need to show another one
      await Promise.all(
        Array.from(assetKeys).map(assetKey => runAffiliation(assetKey))
      );

      // Invalidate queries to refresh vulnerability table
      onScanComplete?.();
    } catch (error) {
      console.error(
        '[useVulnerabilityScanHandlers] Affiliation confirmation failed:',
        error
      );
      toast.error('Failed to confirm affiliation');
    }
  };

  /**
   * Submit selected vulnerabilities to a HackerOne bug bounty program
   *
   * Process:
   * 1. Build job for each vulnerability with hackerone-submit capability
   * 2. Include manual trigger flag and team_handle in config
   * 3. Submit via useBulkCapabilityJob
   *
   * @param vulnerabilities - Vulnerabilities to submit
   * @param params - HackerOne submission parameters (team_handle required)
   */
  const handleSubmitToHackerOne = async (
    vulnerabilities: Risk[],
    params: HackerOneSubmitParams
  ): Promise<void> => {
    try {
      const config: Record<string, string> = {
        manual: 'true',
        team_handle: params.teamHandle,
      };
      if (params.title) {
        config.title = params.title;
      }
      if (params.severityRating) {
        config.severity_rating = params.severityRating;
      }
      if (params.vulnerabilityInformation) {
        config.vulnerability_information = params.vulnerabilityInformation;
      }
      if (params.structuredScopeId !== undefined && params.structuredScopeId !== '') {
        config.structured_scope_id = params.structuredScopeId;
      }
      if (params.weaknessId !== undefined && params.weaknessId !== '') {
        config.weakness_id = params.weaknessId;
      }
      if (params.attachmentS3Keys && params.attachmentS3Keys.length > 0) {
        config.attachment_s3_keys = params.attachmentS3Keys.join(',');
      }

      const jobs = vulnerabilities.map(vulnerability => ({
        assetKey: vulnerability.key,
        capabilities: ['hackerone-submit'],
        config,
      }));

      await runJobs(jobs);
      onScanComplete?.();
    } catch (error) {
      console.error(
        '[useVulnerabilityScanHandlers] HackerOne submit failed:',
        error
      );
      toast.error('Failed to submit to HackerOne');
    }
  };
  return {
    handleRescan,
    handleConfirmAffiliation,
    handleSubmitToHackerOne,
  };
};
